import { CollectionHelper } from 'src/app/helpers/collection-helper';
import {
  SignSizeMaterialDelta,
  SignSizeMaterialDeltaItem,
} from 'src/app/model/helper-interfaces/sign-size-material-delta';
import { ProductSizeAsset } from 'src/app/model/product-size-asset';
import ProductAsset from 'src/app/model/product-asset';
import Board from 'src/app/model/product/board/board';
import BoardConstructionMaterial from 'src/app/model/product/board/board-construction-materials';
import { SignConstructionRequirement } from 'src/app/model/product/board/sign-construction-requirement';
import BoardPart from 'src/app/model/product/board/board-part';

export class SignSizeMaterialDeltaHelper {
  private constructor() {}

  public static processMaterialDelta(
    board: Board,
    boardPart: BoardPart,
    previousSizeId: number,
    newSizeId: number,
    delta: SignSizeMaterialDelta
  ): DeltaProcessingResult {
    let assetsChanged = false;
    if (CollectionHelper.isPopulated(delta.assetsToAdd)) {
      assetsChanged = this.processAssetsToAdd(
        board,
        boardPart.id,
        newSizeId,
        delta.assetsToAdd
      );
    }
    if (CollectionHelper.isPopulated(delta.assetsToArchive)) {
      const changed = this.processAssetsToArchive(
        board,
        boardPart.id,
        previousSizeId,
        delta.assetsToArchive
      );
      assetsChanged = assetsChanged || changed;
    }
    if (CollectionHelper.isPopulated(delta.materialDeltas)) {
      this.processDeltaItems(board, boardPart, delta.materialDeltas);
    }
    return { assetsChanged: assetsChanged };
  }

  private static processAssetsToAdd(
    board: Board,
    boardPartId: number,
    newSizeId: number,
    assetsToAdd: ProductSizeAsset[]
  ): boolean {
    let changed = false;

    assetsToAdd.forEach((psa) => {
      if (!this.otherBoardPartWithSameSize(board, boardPartId, newSizeId)) {
        const productAsset = new ProductAsset();
        productAsset.asset = psa.asset;
        productAsset.formSection = 'sketchUp';
        productAsset.archived = false;
        if (board.productAssets == null) {
          board.productAssets = [];
        }
        board.productAssets.push(productAsset);
        changed = true;
      }
    });

    return changed;
  }

  private static processAssetsToArchive(
    board: Board,
    boardPartId: number,
    previousSizeId: number,
    assetsToArchive: ProductSizeAsset[]
  ): boolean {
    let changed = false;

    assetsToArchive.forEach((psa) => {
      const alreadyPresentAsset = CollectionHelper.isPopulated(
        board.productAssets
      )
        ? board.productAssets.find(
            (pa) => !pa.archived && pa.asset.uuid === psa.asset.uuid
          )
        : null;
      if (
        alreadyPresentAsset != null &&
        !this.otherBoardPartWithSameSize(board, boardPartId, previousSizeId)
      ) {
        alreadyPresentAsset.archived = true;
        changed = true;
      }
    });

    return changed;
  }

  private static otherBoardPartWithSameSize(
    board: Board,
    boardPartId: number,
    sizeId: number
  ): boolean {
    return board.boardParts.some(
      (bp) =>
        bp.id !== boardPartId &&
        bp.signSize != null &&
        bp.signSize.id === sizeId
    );
  }

  private static processDeltaItems(
    board: Board,
    boardPart: BoardPart,
    deltaItems: SignSizeMaterialDeltaItem[]
  ): void {
    for (const item of deltaItems) {
      const index = board.boardConstructionMaterials.findIndex(
        (m) =>
          m.copiedFrom != null &&
          m.copiedFrom.id === item.signSizeConstructionRequirementId
      );
      if (item.amount < 0) {
        this.processRemoveBoardConstructionMaterial(board, index, item);
      } else {
        this.processAddBoardConstructionMaterial(board, boardPart, index, item);
      }
    }
  }

  private static processRemoveBoardConstructionMaterial(
    board: Board,
    existingIndex: number,
    deltaItem: SignSizeMaterialDeltaItem
  ): void {
    if (existingIndex < 0) {
      return;
    }
    const existingMaterial = board.boardConstructionMaterials[existingIndex];
    const newAmount = existingMaterial.amount + deltaItem.amount;
    if (newAmount <= 0) {
      board.boardConstructionMaterials.splice(existingIndex, 1);
    } else {
      existingMaterial.amount = newAmount;
    }
  }

  private static processAddBoardConstructionMaterial(
    board: Board,
    boardPart: BoardPart,
    existingIndex: number,
    deltaItem: SignSizeMaterialDeltaItem
  ): void {
    if (existingIndex < 0) {
      const toCopyFrom = CollectionHelper.isPopulated(
        boardPart.signSize.requiredConstructionMaterials
      )
        ? boardPart.signSize.requiredConstructionMaterials.find(
            (m) => m.id === deltaItem.signSizeConstructionRequirementId
          )
        : null;
      if (toCopyFrom == null) {
        throw new Error(
          `Could not find signSizeConstructionRequirement with id ${deltaItem.signSizeConstructionRequirementId} in sign size with id ${boardPart.signSize}`
        );
      }
      const material = new BoardConstructionMaterial();
      const copy = new SignConstructionRequirement();
      copy.id = toCopyFrom.id;
      material.copiedFrom = copy;
      material.signConstructionMaterialType =
        toCopyFrom.signConstructionMaterialType;
      material.signConstructionMaterialLength =
        toCopyFrom.signConstructionMaterialLength;
      material.description = toCopyFrom.description;
      material.amount = deltaItem.amount;
      board.boardConstructionMaterials.push(material);
    } else {
      board.boardConstructionMaterials[existingIndex].amount +=
        deltaItem.amount;
    }
  }
}

export interface DeltaProcessingResult {
  assetsChanged: boolean;
}
